import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RouteContext } from "context/RouteProvider";
import { environment } from "environment/environment";
import CustomGooglePlacesAutocomplete from "components/Common/auto-complete";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import logo from "../../assets/images/logo.png";
import "./CustomOnboarding.css";

type Step = "shop" | "owner" | "delivery";

interface PickupAddressForm {
  street: string;
  unit: string;
  city: string;
  postalCode: string;
  lat: number | null;
  lng: number | null;
}

const EMPTY_ADDRESS: PickupAddressForm = {
  street: "",
  unit: "",
  city: "",
  postalCode: "",
  lat: null,
  lng: null,
};

const DELIVERY_METHODS = [
  { id: "drop_at_door", label: "Drop at door" },
  { id: "drop_at_mailbox", label: "Drop at mailbox" },
  { id: "drop_at_front_desk", label: "Drop at front desk" },
  { id: "return", label: "Return if delivery fails" },
  { id: "reattempt", label: "Reattempt if delivery fails" },
  { id: "call_me_to_confirm", label: "Call to confirm" },
];

const PREPARATION_OPTIONS = [
  { minutes: 0, label: "Ready immediately" },
  { minutes: 30, label: "30 minutes" },
  { minutes: 60, label: "60 minutes" },
  { minutes: 90, label: "90 minutes" },
  { minutes: 120, label: "120 minutes" },
];

const addressSearchOptions = {
  componentRestrictions: { country: "ca" },
};

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) && value.length <= 120;

/** Canadian NANP: optional +1 / 1, then 10 digits (area + exchange cannot start with 0/1). */
function canadianPhoneDigits(value: string): string {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

function isValidCanadianPhone(value: string): boolean {
  const national = canadianPhoneDigits(value);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(national);
}

function canadianPhoneError(value: string): string {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "Enter a Canadian phone number.";
  }
  if (!isValidCanadianPhone(trimmed)) {
    return "Enter a valid Canadian phone number (e.g. 416-555-1234 or +1 416 555 1234).";
  }
  return "";
}

const formatAddress = (address: PickupAddressForm): string =>
  [address.street, address.unit ? `Unit ${address.unit}` : "", address.city, address.postalCode]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");

const CustomOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [, setIsProtected] = useContext(RouteContext);
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite") || "";

  const [step, setStep] = useState<Step>("shop");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const [shopName, setShopName] = useState("");
  const [shopEmail, setShopEmail] = useState("");
  const [pickup, setPickup] = useState<PickupAddressForm>(EMPTY_ADDRESS);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [deliveryMethod, setDeliveryMethod] = useState("drop_at_door");
  const [preparationMinutes, setPreparationMinutes] = useState(0);

  useEffect(() => {
    document.title = "Custom store setup | Zulu Merchant Portal";
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!invite || invite.length < 20) {
        setError("Missing or invalid invite link. Ask Zulu for a new store invite.");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(
          `${environment.api_url}/api/v1/integrations/custom/invite/${encodeURIComponent(
            invite
          )}`
        );
        const body = await response.json();
        if (!response.ok || body.success === false) {
          setError(body.error || body.message || "This invite link could not be opened.");
          setLoading(false);
          return;
        }
        const data = body.data || {};
        if (data.storeNameHint) {
          setShopName(String(data.storeNameHint));
        }
        if (data.emailLock) {
          setShopEmail(String(data.emailLock));
        }
        setExpiresAt(data.expiresAt || "");
      } catch {
        setError("Could not reach the Zulu API. Try again in a moment.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invite]);

  const selectPickupAddress = async (_label: string, placeId: string) => {
    try {
      const results = await geocodeByPlaceId(placeId);
      const place = results && results[0];
      if (!place) {
        setActionError("Could not read that address. Search again and pick a result.");
        return;
      }
      const components = place.address_components || [];
      const get = (type: string) =>
        components.find((part) => part.types.includes(type))?.long_name || "";
      const streetNumber = get("street_number");
      const route = get("route");
      const street = [streetNumber, route].filter(Boolean).join(" ").trim();
      const city =
        get("locality") ||
        get("sublocality") ||
        get("administrative_area_level_3") ||
        get("postal_town");
      const postalCode = get("postal_code");
      const lat = place.geometry?.location?.lat?.();
      const lng = place.geometry?.location?.lng?.();
      setPickup((current) => ({
        ...current,
        street: street || current.street,
        city: city || current.city,
        postalCode: postalCode || current.postalCode,
        lat: typeof lat === "number" ? lat : current.lat,
        lng: typeof lng === "number" ? lng : current.lng,
      }));
      setActionError("");
    } catch {
      setActionError("Could not read that address. Search again and pick a result.");
    }
  };

  const continueFromShop = (event: React.FormEvent) => {
    event.preventDefault();
    if (shopName.trim().length < 2) {
      setActionError("Enter your shop name.");
      return;
    }
    if (!isValidEmail(shopEmail.trim().toLowerCase())) {
      setActionError("Enter a valid shop email.");
      return;
    }
    if (
      !pickup.street.trim() ||
      !pickup.city.trim() ||
      typeof pickup.lat !== "number" ||
      typeof pickup.lng !== "number"
    ) {
      setActionError("Search and select the default pickup location.");
      return;
    }
    setActionError("");
    setStep("owner");
  };

  const continueFromOwner = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isPersonName(firstName) || !isPersonName(lastName)) {
      setActionError("Enter the shop owner's first and last name.");
      return;
    }
    const phoneValidation = canadianPhoneError(phoneNumber);
    if (phoneValidation) {
      setPhoneError(phoneValidation);
      setActionError(phoneValidation);
      return;
    }
    setPhoneError("");
    if (password.length < 6 || !/\d/.test(password) || password.length > 40) {
      setActionError("Password must be 6 to 40 characters and include a number.");
      return;
    }
    if (password !== confirmPassword) {
      setActionError("Passwords do not match.");
      return;
    }
    setActionError("");
    setStep("delivery");
  };

  const submitRegistration = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!DELIVERY_METHODS.some((method) => method.id === deliveryMethod)) {
      setActionError("Choose a default delivery method.");
      return;
    }
    if (!PREPARATION_OPTIONS.some((option) => option.minutes === preparationMinutes)) {
      setActionError("Choose an order preparation time.");
      return;
    }
    setSubmitting(true);
    setActionError("");
    try {
      const response = await fetch(
        `${environment.api_url}/api/v1/integrations/custom/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            invite,
            storeName: shopName.trim(),
            shopEmail: shopEmail.trim().toLowerCase(),
            email: shopEmail.trim().toLowerCase(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            businessName: shopName.trim(),
            phoneNumber: phoneNumber.trim(),
            password,
            defaultDeliveryMethod: deliveryMethod,
            orderPreparationMinutes: preparationMinutes,
            defaultPickupAddress: {
              street: pickup.street.trim(),
              unit: pickup.unit.trim(),
              city: pickup.city.trim(),
              postalCode: pickup.postalCode.trim().toUpperCase(),
              lat: pickup.lat,
              lng: pickup.lng,
              companyName: shopName.trim(),
              contactName: `${firstName.trim()} ${lastName.trim()}`.trim(),
              contactPhone: phoneNumber.trim(),
            },
          }),
        }
      );
      const body = await response.json();
      if (!response.ok || body.success === false) {
        throw new Error(body.error || body.message || "Could not complete registration.");
      }
      const next = body.data && body.data.next;
      if (next === "verify") {
        setIsProtected(true);
        const verifyEmail =
          (body.data && body.data.email) || shopEmail.trim().toLowerCase();
        navigate("/email-verification", {
          replace: true,
          state: {
            email: verifyEmail,
            merchantPortalReturn: true,
            otpDelivered: body.data ? body.data.otpDelivered !== false : true,
          },
        });
        return;
      }
      window.location.replace("/merchant");
    } catch (requestError) {
      setActionError(
        requestError instanceof Error
          ? requestError.message
          : "Could not complete registration."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = step === "shop" ? 0 : step === "owner" ? 1 : 2;

  return (
    <div className="custom-onboard">
      <div className="custom-onboard__shell">
        <div className="custom-onboard__brand">
          <img src={logo} alt="Zulu" />
          <small>Zulu Merchant Portal</small>
        </div>

        <div className="custom-onboard__card">
          {loading ? (
            <p className="custom-onboard__lede mb-0">Checking your invite…</p>
          ) : null}

          {!loading && error ? (
            <div className="custom-onboard__alert custom-onboard__alert--danger">
              {error}
            </div>
          ) : null}

          {!loading && !error ? (
            <>
              <div className="custom-onboard__steps" aria-hidden="true">
                {[0, 1, 2].map((index) => (
                  <span
                    key={index}
                    className={`custom-onboard__step-pill${
                      index < stepIndex ? " is-done" : index === stepIndex ? " is-active" : ""
                    }`}
                  />
                ))}
              </div>

              {step === "shop" ? (
                <form onSubmit={continueFromShop}>
                  <p className="custom-onboard__kicker">Step 1 of 3 · Shop details</p>
                  <h1 className="custom-onboard__title">Shop details</h1>
                  {actionError ? (
                    <div className="custom-onboard__alert custom-onboard__alert--danger">
                      {actionError}
                    </div>
                  ) : null}

                  <div className="custom-onboard__field">
                    <label className="custom-onboard__label" htmlFor="shop-name">
                      Shop name
                    </label>
                    <input
                      id="shop-name"
                      className="custom-onboard__input"
                      value={shopName}
                      onChange={(event) => setShopName(event.target.value)}
                      placeholder="e.g. Acme Boutique"
                      autoComplete="organization"
                      autoFocus
                      required
                      maxLength={120}
                    />
                  </div>

                  <div className="custom-onboard__field">
                    <label className="custom-onboard__label" htmlFor="shop-email">
                      Shop email
                    </label>
                    <input
                      id="shop-email"
                      className="custom-onboard__input"
                      type="email"
                      value={shopEmail}
                      onChange={(event) => setShopEmail(event.target.value)}
                      placeholder="orders@shop.com"
                      autoComplete="username"
                      required
                      maxLength={120}
                    />
                  </div>

                  <div className="custom-onboard__field">
                    <label className="custom-onboard__label" htmlFor="pickup-search">
                      Default pickup address
                    </label>
                    {pickup.street ? (
                      <div className="custom-onboard__selected-address">
                        <strong>{formatAddress(pickup)}</strong>
                        <button
                          type="button"
                          className="custom-onboard__link-btn"
                          onClick={() => setPickup(EMPTY_ADDRESS)}
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div id="pickup-search" className="custom-onboard__places">
                        <CustomGooglePlacesAutocomplete
                          apiKey={environment.google_maps_key}
                          autocompletionRequest={addressSearchOptions}
                          addressType="pickup"
                          handleAddress={selectPickupAddress}
                        />
                      </div>
                    )}
                    <p className="custom-onboard__hint">
                      Search and select the address drivers should pick up from.
                    </p>
                  </div>

                  {pickup.street ? (
                    <div className="custom-onboard__field">
                      <label className="custom-onboard__label" htmlFor="pickup-unit">
                        Unit (optional)
                      </label>
                      <input
                        id="pickup-unit"
                        className="custom-onboard__input"
                        value={pickup.unit}
                        onChange={(event) =>
                          setPickup((current) => ({
                            ...current,
                            unit: event.target.value,
                          }))
                        }
                        maxLength={40}
                      />
                    </div>
                  ) : null}

                  <div className="custom-onboard__actions">
                    <button
                      type="submit"
                      className="custom-onboard__btn custom-onboard__btn--primary"
                    >
                      Continue
                    </button>
                  </div>
                </form>
              ) : null}

              {step === "owner" ? (
                <form onSubmit={continueFromOwner}>
                  <p className="custom-onboard__kicker">Step 2 of 3 · Shop owner</p>
                  <h1 className="custom-onboard__title">Shop owner</h1>
                  {actionError ? (
                    <div className="custom-onboard__alert custom-onboard__alert--danger">
                      {actionError}
                    </div>
                  ) : null}

                  <div className="custom-onboard__row">
                    <div className="custom-onboard__field">
                      <label className="custom-onboard__label" htmlFor="owner-first">
                        First name
                      </label>
                      <input
                        id="owner-first"
                        className="custom-onboard__input"
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                        autoComplete="given-name"
                        required
                        maxLength={60}
                      />
                    </div>
                    <div className="custom-onboard__field">
                      <label className="custom-onboard__label" htmlFor="owner-last">
                        Last name
                      </label>
                      <input
                        id="owner-last"
                        className="custom-onboard__input"
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                        autoComplete="family-name"
                        required
                        maxLength={60}
                      />
                    </div>
                  </div>

                  <div className="custom-onboard__field">
                    <label className="custom-onboard__label" htmlFor="owner-phone">
                      Phone number (Canada)
                    </label>
                    <input
                      id="owner-phone"
                      className={`custom-onboard__input${
                        phoneError ? " is-invalid" : ""
                      }`}
                      type="tel"
                      inputMode="tel"
                      value={phoneNumber}
                      onChange={(event) => {
                        setPhoneNumber(event.target.value);
                        if (phoneError) {
                          setPhoneError("");
                        }
                      }}
                      onBlur={() => setPhoneError(canadianPhoneError(phoneNumber))}
                      autoComplete="tel"
                      placeholder="416-555-1234"
                      maxLength={20}
                      required
                      aria-invalid={Boolean(phoneError)}
                      aria-describedby={phoneError ? "owner-phone-error" : "owner-phone-hint"}
                    />
                    {phoneError ? (
                      <p id="owner-phone-error" className="custom-onboard__field-error">
                        {phoneError}
                      </p>
                    ) : (
                      <p id="owner-phone-hint" className="custom-onboard__hint">
                        Canadian numbers only. Example: 416-555-1234 or +1 416 555 1234
                      </p>
                    )}
                  </div>

                  <div className="custom-onboard__row">
                    <div className="custom-onboard__field">
                      <label className="custom-onboard__label" htmlFor="owner-password">
                        Password
                      </label>
                      <div className="custom-onboard__password">
                        <input
                          id="owner-password"
                          className="custom-onboard__input"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          autoComplete="new-password"
                          required
                          minLength={6}
                          maxLength={40}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          onClick={() => setShowPassword((current) => !current)}
                        >
                          <PasswordEye open={showPassword} />
                        </button>
                      </div>
                    </div>
                    <div className="custom-onboard__field">
                      <label className="custom-onboard__label" htmlFor="owner-confirm">
                        Confirm password
                      </label>
                      <div className="custom-onboard__password">
                        <input
                          id="owner-confirm"
                          className="custom-onboard__input"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(event) => setConfirmPassword(event.target.value)}
                          autoComplete="new-password"
                          required
                          minLength={6}
                          maxLength={40}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          aria-label={
                            showConfirmPassword
                              ? "Hide confirm password"
                              : "Show confirm password"
                          }
                          onClick={() =>
                            setShowConfirmPassword((current) => !current)
                          }
                        >
                          <PasswordEye open={showConfirmPassword} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="custom-onboard__actions">
                    <button
                      type="button"
                      className="custom-onboard__btn custom-onboard__btn--ghost"
                      onClick={() => {
                        setActionError("");
                        setStep("shop");
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="custom-onboard__btn custom-onboard__btn--primary"
                    >
                      Continue
                    </button>
                  </div>
                </form>
              ) : null}

              {step === "delivery" ? (
                <form onSubmit={submitRegistration}>
                  <p className="custom-onboard__kicker">Step 3 of 3 · Delivery setup</p>
                  <h1 className="custom-onboard__title">Delivery setup</h1>
                  {actionError ? (
                    <div className="custom-onboard__alert custom-onboard__alert--danger">
                      {actionError}
                    </div>
                  ) : null}

                  <p className="custom-onboard__label">Default delivery method</p>
                  <div className="custom-onboard__choices" role="radiogroup" aria-label="Default delivery method">
                    {DELIVERY_METHODS.map((option) => {
                      const selected = deliveryMethod === option.id;
                      return (
                        <label
                          key={option.id}
                          className={`custom-onboard__choice${selected ? " is-selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name="delivery-method"
                            value={option.id}
                            checked={selected}
                            onChange={() => setDeliveryMethod(option.id)}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>

                  <p className="custom-onboard__label">Order preparation time</p>
                  <p className="custom-onboard__hint" style={{ marginBottom: 10 }}>
                    Pickup is ready at the order time plus this preparation time.
                  </p>
                  <div
                    className="custom-onboard__choices"
                    role="radiogroup"
                    aria-label="Order preparation time"
                  >
                    {PREPARATION_OPTIONS.map((option) => {
                      const selected = preparationMinutes === option.minutes;
                      return (
                        <label
                          key={option.minutes}
                          className={`custom-onboard__choice${selected ? " is-selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name="preparation-time"
                            value={option.minutes}
                            checked={selected}
                            onChange={() => setPreparationMinutes(option.minutes)}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>

                  <div className="custom-onboard__actions">
                    <button
                      type="button"
                      className="custom-onboard__btn custom-onboard__btn--ghost"
                      disabled={submitting}
                      onClick={() => {
                        setActionError("");
                        setStep("owner");
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="custom-onboard__btn custom-onboard__btn--primary"
                      disabled={submitting}
                    >
                      {submitting ? "Creating account…" : "Create account"}
                    </button>
                  </div>
                </form>
              ) : null}
            </>
          ) : null}
        </div>

        {expiresAt && !loading && !error ? (
          <p className="custom-onboard__meta">
            Invite expires {new Date(expiresAt).toLocaleString()}
          </p>
        ) : null}
      </div>
    </div>
  );
};

function isPersonName(value: string): boolean {
  return /^[a-zA-Z][a-zA-Z .'-]{0,59}$/.test(value.trim());
}

const PasswordEye: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
    </svg>
  );

export default CustomOnboarding;
